/**
 * HVACExportModal - Modal for exporting HVAC data
 * Provides CSV, JSON, and PDF export options
 */

import React, { useState } from 'react';
import { downloadHVACCSVs, downloadHVACJSON } from '../../utils/hvacExport';
import { downloadHVACReport } from '../../utils/hvacPdfReport';

// Icons
const CSVIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="16" y2="17" />
  </svg>
);

const JSONIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <path d="M8 13h2v4c0 1-1 2-2 2" />
    <path d="M14 13h2v4c0 1 1 2 2 2" />
  </svg>
);

const PDFIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <path d="M9 15v-2h2c1 0 2 .5 2 1.5s-1 1.5-2 1.5H9z" />
  </svg>
);

const ShareIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4caf50" strokeWidth="3">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const LoadingIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spin">
    <circle cx="12" cy="12" r="10" strokeOpacity="0.3" />
    <path d="M12 2a10 10 0 0 1 10 10" />
    <style>{`
      @keyframes spin { to { transform: rotate(360deg); } }
      .spin { animation: spin 1s linear infinite; }
    `}</style>
  </svg>
);

export default function HVACExportModal({
  activeFloor,
  projectInfo,
  canvasRef,
  onClose,
  onShare,
  isMobile = false,
}) {
  const [exporting, setExporting] = useState(null);
  const [completed, setCompleted] = useState({});
  const [error, setError] = useState(null);

  const handleExportCSV = async () => {
    setExporting('csv');
    setError(null);
    try {
      await downloadHVACCSVs(activeFloor, projectInfo);
      setCompleted(prev => ({ ...prev, csv: true }));
      setTimeout(() => setCompleted(prev => ({ ...prev, csv: false })), 3000);
    } catch (err) {
      console.error('CSV export failed:', err);
      setError('Failed to export CSV files');
    }
    setExporting(null);
  };

  const handleExportJSON = async () => {
    setExporting('json');
    setError(null);
    try {
      downloadHVACJSON(activeFloor, projectInfo);
      setCompleted(prev => ({ ...prev, json: true }));
      setTimeout(() => setCompleted(prev => ({ ...prev, json: false })), 3000);
    } catch (err) {
      console.error('JSON export failed:', err);
      setError('Failed to export JSON file');
    }
    setExporting(null);
  };

  const handleExportPDF = async () => {
    setExporting('pdf');
    setError(null);
    try {
      await downloadHVACReport(activeFloor, projectInfo, canvasRef);
      setCompleted(prev => ({ ...prev, pdf: true }));
      setTimeout(() => setCompleted(prev => ({ ...prev, pdf: false })), 3000);
    } catch (err) {
      console.error('PDF export failed:', err);
      setError('Failed to generate PDF report');
    }
    setExporting(null);
  };

  const handleShare = () => {
    onShare?.();
    onClose();
  };

  const ExportButton = ({ icon: Icon, title, description, format, onClick, color }) => (
    <button
      onClick={onClick}
      disabled={exporting !== null}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '14px',
        padding: '16px',
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '10px',
        cursor: exporting ? 'wait' : 'pointer',
        width: '100%',
        textAlign: 'left',
        transition: 'all 0.2s ease',
        opacity: exporting && exporting !== format ? 0.5 : 1,
      }}
      onMouseEnter={(e) => {
        if (!exporting) {
          e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
          e.currentTarget.style.borderColor = color;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
      }}
    >
      <div
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '10px',
          background: `${color}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color,
          flexShrink: 0,
        }}
      >
        <Icon />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '4px',
        }}>
          <span style={{
            color: '#e0e0e0',
            fontSize: '14px',
            fontWeight: '600',
          }}>
            {title}
          </span>
          {exporting === format && <LoadingIcon />}
          {completed[format] && <CheckIcon />}
        </div>
        <span style={{
          color: '#8090a0',
          fontSize: '12px',
          lineHeight: '1.4',
        }}>
          {description}
        </span>
      </div>
    </button>
  );

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: isMobile ? 0 : '20px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: '#1a2332',
          borderRadius: isMobile ? '12px 12px 0 0' : '12px',
          width: isMobile ? '100%' : '420px',
          maxHeight: '90vh',
          overflow: 'auto',
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(0,200,255,0.05)',
        }}>
          <span style={{ color: '#00c8ff', fontWeight: '600', fontSize: '14px' }}>
            Export HVAC Data
          </span>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,100,100,0.15)',
              border: 'none',
              color: '#ff6666',
              fontSize: '16px',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {error && (
          <div style={{
            padding: '10px 14px',
            background: 'rgba(244, 67, 54, 0.15)',
            border: '1px solid rgba(244, 67, 54, 0.3)',
            borderRadius: '8px',
            color: '#f44336',
            fontSize: '12px',
          }}>
            {error}
          </div>
        )}

        <ExportButton
          icon={CSVIcon}
          title="Export CSV Files"
          description="Room data, equipment, and ductwork as separate CSV files. Compatible with Excel and Manual J software."
          format="csv"
          onClick={handleExportCSV}
          color="#4caf50"
        />

        <ExportButton
          icon={JSONIcon}
          title="Export JSON"
          description="Complete HVAC data in structured JSON format. Ready for API integration with load calculation software."
          format="json"
          onClick={handleExportJSON}
          color="#2196f3"
        />

        <ExportButton
          icon={PDFIcon}
          title="Generate PDF Report"
          description="Professional multi-page report with floor plan, room schedules, and equipment lists."
          format="pdf"
          onClick={handleExportPDF}
          color="#f44336"
        />

        {onShare && (
          <>
            <div style={{
              height: '1px',
              background: 'rgba(255,255,255,0.1)',
              margin: '8px 0',
            }} />

            <ExportButton
              icon={ShareIcon}
              title="Share HVAC Report"
              description="Generate a shareable link to the HVAC report that can be viewed in any browser."
              format="share"
              onClick={handleShare}
              color="#9c27b0"
            />
          </>
        )}

        {/* Data Summary */}
        <div style={{
          marginTop: '8px',
          padding: '12px 14px',
          background: 'rgba(0,0,0,0.2)',
          borderRadius: '8px',
        }}>
          <div style={{
            fontSize: '10px',
            color: '#6080a0',
            textTransform: 'uppercase',
            marginBottom: '8px',
            fontWeight: '600',
          }}>
            Data Summary
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '8px',
            fontSize: '11px',
          }}>
            <DataItem label="Rooms" value={activeFloor?.rooms?.length || 0} />
            <DataItem label="Equipment" value={activeFloor?.equipment?.length || 0} />
            <DataItem label="Ducts" value={activeFloor?.ducts?.length || 0} />
            <DataItem label="Supply Vents" value={(activeFloor?.vents || []).filter(v => v.type?.startsWith('supply')).length} />
            <DataItem label="Return Grilles" value={(activeFloor?.vents || []).filter(v => v.type?.startsWith('return')).length} />
            <DataItem label="Fittings" value={activeFloor?.fittings?.length || 0} />
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

function DataItem({ label, value }) {
  return (
    <div>
      <div style={{ color: '#8090a0' }}>{label}</div>
      <div style={{ color: '#e0e0e0', fontWeight: '600', fontSize: '14px' }}>{value}</div>
    </div>
  );
}
